// Shareable image of a lap (time + sectors + circuit outline), shared through
// the Web Share API where available, otherwise downloaded.

import type { Track } from '../track/track';
import { formatTime } from './hud';

export interface ShareLap {
  trackName: string;
  lapTime: number;
  sectors: Array<number | undefined>;
  date: Date;
}

const W = 1080;
const H = 1350;

export function drawShareCard(track: Track, lap: ShareLap): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d')!;
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#141a2a');
  bg.addColorStop(1, '#06070b');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'italic 900 96px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('FONE', 80, 170);
  const foneWidth = ctx.measureText('FONE ').width;
  ctx.fillStyle = '#e10600';
  ctx.fillText('GP', 80 + foneWidth, 170);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '600 44px system-ui, -apple-system, sans-serif';
  ctx.fillText(`${lap.trackName} · Time trial`, 80, 240);

  // Circuit outline.
  const pts = track.points;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  const box = { x: 80, y: 300, w: W - 160, h: 560 };
  const scale = Math.min(box.w / (maxX - minX), box.h / (maxZ - minZ));
  const ox = box.x + (box.w - (maxX - minX) * scale) / 2;
  const oy = box.y + (box.h + (maxZ - minZ) * scale) / 2;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 12;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  pts.forEach((p, i) => {
    const x = ox + (p.x - minX) * scale;
    const y = oy - (p.z - minZ) * scale;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffcc33';
  ctx.font = '700 46px system-ui, -apple-system, sans-serif';
  ctx.fillText('LAP TIME', W / 2, 980);
  ctx.fillStyle = '#ffffff';
  ctx.font = '800 150px system-ui, -apple-system, sans-serif';
  ctx.fillText(formatTime(lap.lapTime), W / 2, 1130);

  ctx.font = '600 42px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  const sectorText = lap.sectors.map((t, i) => `S${i + 1} ${t === undefined ? '-' : t.toFixed(3)}`).join('    ');
  ctx.fillText(sectorText, W / 2, 1210);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = '500 34px system-ui, -apple-system, sans-serif';
  ctx.fillText(`${lap.date.toLocaleDateString()} · ${location.host}`, W / 2, 1290);
  return c;
}

export async function shareLap(track: Track, lap: ShareLap): Promise<void> {
  const canvas = drawShareCard(track, lap);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return;
  const file = new File([blob], 'fone-gp-lap.png', { type: 'image/png' });
  const text = `${formatTime(lap.lapTime)} around ${lap.trackName} in Fone GP`;
  try {
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: 'Fone GP lap', text });
      return;
    }
  } catch (e) {
    if ((e as DOMException).name === 'AbortError') return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'fone-gp-lap.png';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
